import { Component, ElementRef, OnDestroy, OnInit, ViewChild, ChangeDetectionStrategy } from '@angular/core'
import { ActivatedRoute, Router } from '@angular/router'
import * as d3 from 'd3'
import { forkJoin, of, Subject } from 'rxjs'
import { catchError, filter, switchMap, take, takeUntil } from 'rxjs/operators'
import { AccessLevelService, AccountPermission } from 'src/app/services/access-level.service'
import { ApiService } from 'src/app/services/api.service'
import { UiService } from 'src/app/services/ui.service'
import { handleArpioError } from 'src/utils/handle-error'
import { MenuItem } from 'primeng/api'
import { AccountService } from '../../services/account.service'
import {
  AppBreakdownRowVm,
  CloudFilter,
  DailyBreakdownRowVm,
  DailyPointVm,
  MonthlyPointVm,
  UsageDashboardVm,
} from './usage-billing.model'
import { USAGE_DATA_START_DATE, USAGE_DATA_START_MONTH, UsageBillingService } from './usage-billing.service'

// Chart palette — hardcoded hex mirroring _style-vars.scss (D3 can't read SCSS).
const ACCENT = '#00a9be' // $accent-color
const AMBER = '#f1b122' // $warn-color — over-contract (informational for Flex)
const GRAY = '#6b7280' // $light-gray
const BASELINE = '#9aa0a6'
const AXIS = '#6b7280'

// Figures the reader compares against the tiles are grouped in full; axis ticks compact to
// SI once they reach four digits, where exact labels crowd the plot.
const GROUPED_COUNT_FORMAT = d3.format(',d')
const AXIS_SI_FORMAT = d3.format('~s')
/** Above this multiple of the observed peak, the contracted cap leaves the y domain. */
const CAP_IN_SCALE_RATIO = 2
const Y_TICK_COUNT = 6
/** Reproduces the axis title's original offset at the narrowest margin. */
const AXIS_TITLE_INSET = 14

function formatCount(value: number | null | undefined): string {
  return value == null ? '—' : GROUPED_COUNT_FORMAT(value)
}

// Ranges up to ~2 months render daily; longer ranges roll up to monthly.
const DAILY_MAX_SPAN_DAYS = 62
// Daily cycles fetched on demand for one period. The daily view is capped at the span
// above, so it never needs more than this — the chart plots at most ~2 months of days.
const MAX_FETCHED_DAILY_CYCLES = 3
// Breakdown cycles fetched on demand. Unlike the chart, the Applications tab resolves the
// period's peak-billable day across the *whole* selection, so a 6/12-month preset has to
// pull every cycle it spans or the peak silently resolves against only the recent few. The
// widest preset is 12 months; an anniversary cycle can make that touch one extra cycle.
const MAX_FETCHED_BREAKDOWN_CYCLES = 13

const MONTH_ABBRS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

/** 'YYYY-MM' -> 'Mar 2026'. */
function monthLabel(monthKey: string): string {
  const [year, month] = monthKey.split('-').map(Number)
  return `${MONTH_ABBRS[month - 1]} ${year}`
}

// Rows shown in the Application breakdown table, applied after filtering.
const APP_BREAKDOWN_LIMIT = 10

// Unique ID counter for SVG defs (clip-path) to avoid collisions, as in rpo-chart.
let instanceCounter = 0

type Granularity = 'daily' | 'monthly'
type Tab = 'trend' | 'apps'

interface ChartPoint {
  x: string
  value: number
  over: boolean
  tooltip: string[]
}
interface ChartSeries {
  points: ChartPoint[]
  contracted: number | null
  /** False when the cap sits too far above the data to share the y axis with it. */
  capInScale: boolean
  yTitle: string
}

/**
 * Usage & Billing dashboard.
 *
 * A single dashboard-wide filter (period + cloud) drives everything: the KPI
 * tiles, the High Water Mark Trend chart, and the Application breakdown — which
 * live under a sticky filter bar and split into "Usage trend" / "Applications"
 * tabs. The period is a preset or an exact start–end range (Azure bills on exact
 * bill-period dates, not calendar months). Data comes from the live usage
 * endpoints via UsageBillingService.
 */
@Component({
  selector: 'app-usage-billing',
  templateUrl: './usage-billing.component.html',
  styleUrls: ['./usage-billing.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class UsageBillingComponent implements OnInit, OnDestroy {
  @ViewChild('trendChart', { static: false }) trendChart?: ElementRef<HTMLDivElement>
  @ViewChild('headerContainer', { static: false }) headerContainer?: ElementRef<HTMLDivElement>
  @ViewChild('controlsBar', { static: false }) controlsBar?: ElementRef<HTMLDivElement>

  vm: UsageDashboardVm | null = null
  loading = true
  loadError = false
  // Set while earlier cycles are being fetched for a newly selected period. Scoped to
  // the chart and the breakdown table so the tiles and the filter bar stay live.
  chartLoading = false
  appsLoading = false

  // Flattened, date-sorted daily series across every cycle fetched so far — sliced by
  // period. Starts as the current cycle and grows as earlier periods are selected.
  private allDaily: DailyPointVm[] = []
  // Cycles already pulled into allDaily, keyed the way the endpoint names them (the
  // month the cycle starts in), so a period revisited twice doesn't refetch.
  private loadedCycles = new Set<string>()
  // Selectable range floor. Spans the monthly series, which reaches back further than
  // the daily data, so multi-month ranges are pickable.
  minDate = ''
  maxDate = ''
  // Earliest day held in allDaily. Selecting further back fetches the cycles that cover
  // it, so this only stops moving at the end of the backend's snapshot retention — past
  // that the daily view floors here rather than plotting a short series under a much
  // wider label.
  private dailyFloor = ''
  // Per-application detail across every cycle fetched so far, and the earliest day it
  // covers. Tracked apart from the daily series: the two are fetched from different
  // endpoints on different triggers, so their coverage can differ.
  private breakdownRows: DailyBreakdownRowVm[] = []
  private breakdownFloor = ''
  private loadedBreakdownCycles = new Set<string>()

  // --- global filters (sticky top bar) --------------------------------------
  cloud: CloudFilter = 'ALL'
  readonly cloudOptions: { value: CloudFilter; label: string }[] = [
    { value: 'ALL', label: 'All' },
    { value: 'AWS', label: 'AWS' },
    { value: 'AZURE', label: 'Azure' },
  ]

  readonly periodPresets: { key: string; label: string }[] = [
    { key: 'bill', label: 'This bill period' },
    { key: '30d', label: '30 days' },
    { key: '3m', label: '3 months' },
    { key: '6m', label: '6 months' },
    { key: '12m', label: '12 months' },
    { key: 'custom', label: 'Custom' },
  ]
  periodKey = 'bill'
  periodStart = ''
  periodEnd = ''
  private granularityOverride: Granularity | null = null

  activeTab: Tab = 'trend'
  // The tab in use when a trend-less selection forced Applications, so widening the
  // selection again returns the user to where they were.
  private tabBeforeAppsOnly: Tab | null = null

  expandedApps = new Set<string>()
  appSearch = ''

  // Memo for the app-breakdown filter passes; see ensureAppFilterCache().
  private _appFilterSource: AppBreakdownRowVm[] | null = null
  private _appFilterKey: [CloudFilter, string] | null = null
  private _qualifyingApps: AppBreakdownRowVm[] = []
  private _displayedApps: AppBreakdownRowVm[] = []

  /**
   * The split button's dropdown. Both items are the same server export over different date
   * ranges — the whole selected period, or just the day the table reports — so the two files
   * carry identical columns and reconcile against each other. Neither is capped to the ten
   * rows on screen: the footer note points at the export for what the table leaves out.
   */
  private readonly _exportMenuItems: MenuItem[] = [
    {
      label: 'All applications<div class="menu-desc">Every application, every day of the selected period</div>',
      escape: false,
      command: () => this.exportCsv(),
    },
    {
      label: '',
      escape: false,
      command: () => this.exportBreakdownDayCsv(),
    },
  ]
  /** Day label the second item was last composed for; null forces the first build. */
  private _exportMenuDayLabel: string | null = null

  /** Built once: bound by both the chart overlay and the breakdown table. */
  readonly noUsageDataMessage = `No usage data available before ${monthLabel(USAGE_DATA_START_MONTH)}.`

  private readonly clipId = `usage-trend-clip-${++instanceCounter}`
  private resizeObserver?: ResizeObserver
  private headerObserver?: ResizeObserver
  private destroy$ = new Subject<void>()

  constructor(
    private el: ElementRef<HTMLElement>,
    private router: Router,
    private route: ActivatedRoute,
    private accountService: AccountService,
    private accessLevelService: AccessLevelService,
    private apiService: ApiService,
    public uiService: UiService,
    private usageBillingService: UsageBillingService
  ) {}

  ngOnInit() {
    // AccountService holds the previous account's entitlement until the next load resolves and
    // accountIdStream replays that account's id, so an in-app switch would read Flex off the
    // wrong account. Waiting for the id in the URL pins the verdict to the account being viewed.
    // A null routed id means we're mounted outside an account route (/test_page), where any id
    // will do.
    const routedAccountId = this.route.snapshot.paramMap.get('accountId')
    this.accountService.accountIdStream
      .pipe(
        filter((accountId) => !!accountId && (!routedAccountId || accountId === routedAccountId)),
        take(1),
        switchMap(() => this.accessLevelService.hasAccountPermission(AccountPermission.READ_BILLABLE_RESOURCE_REPORT)),
        take(1),
        takeUntil(this.destroy$)
      )
      .subscribe((allowed) => {
        // The usage endpoints also enforce READ_BILLABLE_RESOURCE_REPORT, so a user
        // without it would land here only to have every request 403. Redirect instead
        // of rendering a page that can never load.
        if (!allowed) {
          this.router.navigate(['/', this.accountService.accountId])
          return
        }
        // Every figure here describes consumption billed in arrears, which is only how a Flex
        // account is billed. Until Fixed billing has its own dashboard the page doesn't exist
        // for anyone else, so say so rather than showing numbers that misdescribe their bill.
        // skipLocationChange leaves the typed URL in the address bar, matching what an
        // unmatched URL does today — a mistyped one stays there to be corrected.
        if (!this.accountService.flexBillingEnabled) {
          this.router.navigateByUrl('/not-found', { skipLocationChange: true })
          return
        }
        this.loadDashboard()
      })
  }

  ngOnDestroy() {
    this.destroy$.next()
    this.destroy$.complete()
    this.resizeObserver?.disconnect()
    this.headerObserver?.disconnect()
  }

  private loadDashboard() {
    const accountId = this.accountService.accountId
    if (!accountId) return
    this.loading = true
    this.loadError = false
    // UTC, matching the server: billing cycles are UTC-anchored, so a local-time key
    // asks a user ahead of UTC for next month's cycle for the last hours of this one.
    const month = new Date().toISOString().slice(0, 7)
    this.fetchDashboard(accountId, month, true)
  }

  /**
   * Fetch and apply one billing cycle's dashboard.
   *
   * `month` selects the cycle that *starts* in that calendar month. For an anniversary-billed
   * account before its anchor day, the current calendar month names a cycle that hasn't begun
   * yet — the server reports `isCurrent === false` and returns no data. The in-progress cycle
   * started the previous month, so `retryPrevIfNotCurrent` re-requests it once. Without this the
   * page shows an empty future cycle and never fetches the real cycle's peak day / apps.
   */
  private fetchDashboard(accountId: string, month: string, retryPrevIfNotCurrent: boolean) {
    this.usageBillingService
      .loadDashboard(accountId, month)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (vm) => {
          if (retryPrevIfNotCurrent && !vm.billingCycle.isCurrent) {
            const [year, m] = month.split('-').map(Number)
            const prev = this.iso(new Date(Date.UTC(year, m - 2, 1))).slice(0, 7)
            this.fetchDashboard(accountId, prev, false)
            return
          }
          this.applyDashboard(vm)
        },
        error: (err) => {
          this.loading = false
          this.loadError = true
          handleArpioError(window.location.href, this.apiService, this.uiService)(err)
        },
      })
  }

  private applyDashboard(vm: UsageDashboardVm) {
    this.vm = vm
    this.allDaily = vm.daily
    // Keyed off the cycle's actual start, not billingCycle.monthKey — see cycleKeyForDate.
    // Falls back to monthKey when the cycle has no bounds (no records + no server bounds),
    // where cycleKeyForDate can't derive a start month.
    const currentKey = this.cycleKeyForDate(vm.billingCycle.start) || vm.billingCycle.monthKey
    this.loadedCycles.clear()
    this.loadedCycles.add(currentKey)
    this.loadedBreakdownCycles.clear()
    this.loadedBreakdownCycles.add(currentKey)
    this.breakdownRows = vm.dailyBreakdown
    const lastDaily = this.allDaily[this.allDaily.length - 1]?.date ?? ''
    this.dailyFloor = this.allDaily[0]?.date ?? ''
    this.breakdownFloor = vm.dailyBreakdown[0]?.date ?? ''
    // Bounded by the trend window the endpoint covered, not by the floored series: a
    // range reaching past the data start stays selectable and simply reports no data.
    // Anything narrower would clamp every multi-month preset into the daily month and
    // collapse the trend to a single point.
    const windowStart = vm.trendWindowStart ? `${vm.trendWindowStart}-01` : ''
    this.minDate = [this.dailyFloor, windowStart].filter(Boolean).sort()[0] ?? ''
    // A cycle whose first daily snapshot hasn't landed yet still has monthly history.
    this.maxDate = lastDaily || vm.billingCycle.end
    const r = this.rangeForPreset('bill')
    this.periodStart = r.start
    this.periodEnd = r.end
    this.syncTabToTrendAvailability()
    this.loading = false
    // Let *ngIf="vm" build the chart DOM, then wire observers + draw.
    setTimeout(() => {
      this.setupResizeObserver()
      this.observeHeaderHeight()
      this.renderAll()
    }, 0)
  }

  // Publish the "chrome" height (sticky header + the controls bar — filter + tabs —
  // that pins just below it) as CSS vars so the tab-pane fills the remaining
  // viewport and the controls land under the pinned header at max scroll.
  // Re-measures as the filter bar wraps.
  private observeHeaderHeight() {
    const header = this.headerContainer?.nativeElement
    if (!header) return
    const publish = () => {
      const controls = this.controlsBar?.nativeElement
      const controlsH = controls ? controls.offsetHeight : 0
      // --usage-header-h: sticky offset for the controls bar (pins below the header).
      // --usage-chrome-h: header + controls, the always-pinned height the pane sizes against.
      this.el.nativeElement.style.setProperty('--usage-header-h', `${header.offsetHeight}px`)
      this.el.nativeElement.style.setProperty('--usage-chrome-h', `${header.offsetHeight + controlsH}px`)
    }
    this.headerObserver?.disconnect()
    this.headerObserver = new ResizeObserver(() => publish())
    this.headerObserver.observe(header)
    this.controlsBar && this.headerObserver.observe(this.controlsBar.nativeElement)
    publish()
  }

  // --- date helpers ---------------------------------------------------------

  private iso(d: Date): string {
    return d.toISOString().slice(0, 10)
  }
  // An account with no daily records and no server-supplied cycle bounds leaves the
  // bounds empty, where an unguarded compare would clamp every date to ''.
  private clampToData(iso: string): string {
    if (this.minDate && iso < this.minDate) return this.minDate
    if (this.maxDate && iso > this.maxDate) return this.maxDate
    return iso
  }
  private rangeForPreset(key: string): { start: string; end: string } {
    const end = this.maxDate
    if (key === 'bill') {
      // Server-supplied cycle bounds (Azure bills on anniversary dates, not calendar
      // months). Mid-cycle the end is in the future, so clamping lands it on the last
      // day that actually has data.
      const cycle = this.vm?.billingCycle
      return { start: this.clampToData(cycle?.start ?? ''), end: this.clampToData(cycle?.end ?? '') }
    }
    const d = new Date(end + 'T00:00:00Z')
    if (key === '30d') d.setUTCDate(d.getUTCDate() - 29)
    else if (key === '3m') d.setUTCMonth(d.getUTCMonth() - 3)
    else if (key === '6m') d.setUTCMonth(d.getUTCMonth() - 6)
    else if (key === '12m') d.setUTCMonth(d.getUTCMonth() - 12)
    return { start: this.clampToData(this.iso(d)), end }
  }
  // Formatted straight from the ISO date rather than looked up in allDaily, so
  // multi-month ranges (whose start predates the loaded daily data) still label.
  private formatDay(iso: string): string {
    if (!iso) return ''
    const [y, m, d] = iso.split('-').map(Number)
    return `${MONTH_ABBRS[m - 1]} ${d}, ${y}`
  }
  /**
   * Header subtitle: the cycle the page covers, and whether it has closed.
   *
   * The billing basis is deliberately not named. It is per-account — Fixed and Flex
   * differ, and the metering anchor is the calendar month for AWS Marketplace and
   * direct accounts but the subscription anniversary for Azure Marketplace — so any
   * wording here would be right for some accounts and wrong for others.
   */
  get billingCycleSubtitle(): string {
    const cycle = this.vm?.billingCycle
    if (!cycle) return ''
    // An open cycle's high water mark can still rise, so say so rather than letting
    // the number read as final.
    const state = cycle.isCurrent ? ' (in progress)' : ''
    return `Billing cycle: ${cycle.label}${state}`
  }

  /**
   * Start of the window actually rendered. Daily granularity can only show what the
   * loaded billing cycle covers, so it floors at `dailyFloor`; monthly uses the full
   * selection. Deliberately NOT fed back into `spanDays` — that picks the granularity,
   * and flooring it first would make the two circular.
   */
  private get effectiveStart(): string {
    if (this.granularity !== 'daily' || !this.dailyFloor) return this.periodStart
    // A range ending before the daily window has no daily data to floor into; flooring
    // anyway would push the start past the end and label the chart backwards.
    if (this.dailyRangeUnavailable) return this.periodStart
    return this.periodStart < this.dailyFloor ? this.dailyFloor : this.periodStart
  }
  /** True when the selected range predates the daily window entirely — nothing to plot. */
  get dailyRangeUnavailable(): boolean {
    return this.granularity === 'daily' && !!this.dailyFloor && !!this.periodEnd && this.periodEnd < this.dailyFloor
  }
  /** True when the daily view is showing only part of the selected range. */
  get dailyRangeTruncated(): boolean {
    return (
      this.granularity === 'daily' &&
      !!this.dailyFloor &&
      this.periodStart < this.dailyFloor &&
      !this.dailyRangeUnavailable
    )
  }

  /** First day the daily series can show, for the truncation notice. */
  get dailyFloorLabel(): string {
    return this.formatDay(this.dailyFloor)
  }

  /**
   * True when the whole selection predates Arpio's first usage records, so there is
   * nothing to draw or list. A range that merely *starts* before the floor still holds
   * real days and renders normally — the shortfall isn't worth a message there.
   */
  get rangeBeforeUsageData(): boolean {
    return !!this.periodEnd && this.periodEnd < USAGE_DATA_START_DATE
  }

  get periodRangeLabel(): string {
    if (!this.periodStart || !this.periodEnd) return ''
    return `${this.formatDay(this.effectiveStart)} – ${this.formatDay(this.periodEnd)}`
  }

  // --- Applications-only mode ----------------------------------------------

  /**
   * True when the selection resolves to a single point in time, which has no trend to
   * plot — one dot on an axis reads as a broken chart rather than a flat one. Later
   * point-in-time filters join this getter so every trigger collapses the view the
   * same way.
   */
  get isPointInTimeSelection(): boolean {
    return !!this.periodStart && this.periodStart === this.periodEnd
  }

  /** Whether the Usage trend tab can be opened. */
  get trendAvailable(): boolean {
    return !this.isPointInTimeSelection
  }

  /** Why the Usage trend tab is disabled. Empty when enabled. */
  get trendDisabledReason(): string {
    return this.trendAvailable ? '' : 'Select a range spanning at least two days to view the usage trend.'
  }

  /**
   * Collapse to Applications while the trend has nothing to draw, and back out after.
   * Reports whether the tab changed: a switch remounts the pane behind its *ngIf, so the
   * caller owes the new chart element the same rebind setTab() performs.
   */
  private syncTabToTrendAvailability(): boolean {
    if (!this.trendAvailable) {
      if (this.activeTab !== 'apps') {
        this.tabBeforeAppsOnly = this.activeTab
        this.activeTab = 'apps'
        return true
      }
    } else if (this.tabBeforeAppsOnly) {
      this.activeTab = this.tabBeforeAppsOnly
      this.tabBeforeAppsOnly = null
      return true
    }
    return false
  }

  // --- filter actions -------------------------------------------------------

  setPeriodPreset(key: string) {
    this.periodKey = key
    if (key !== 'custom') {
      const r = this.rangeForPreset(key)
      this.periodStart = r.start
      this.periodEnd = r.end
    }
    this.granularityOverride = null
    this.expandedApps.clear()
    this.scheduleRender()
  }
  // min/max on a date input only constrain the picker — a typed or cleared value still
  // commits, and an empty bound would blank the range label while the chart silently
  // widened to everything. Rejected entries are written back to the field because the
  // one-way [value] binding won't repaint when the value resolves to what it already held.
  setPeriodStart(e: Event) {
    const input = e.target as HTMLInputElement
    this.periodStart = input.value ? this.clampToData(input.value) : this.periodStart
    input.value = this.periodStart
    if (this.periodStart > this.periodEnd) this.periodEnd = this.periodStart
    this.periodKey = 'custom'
    this.granularityOverride = null
    this.expandedApps.clear()
    this.scheduleRender()
  }
  setPeriodEnd(e: Event) {
    const input = e.target as HTMLInputElement
    this.periodEnd = input.value ? this.clampToData(input.value) : this.periodEnd
    input.value = this.periodEnd
    if (this.periodEnd < this.periodStart) this.periodStart = this.periodEnd
    this.periodKey = 'custom'
    this.granularityOverride = null
    this.expandedApps.clear()
    this.scheduleRender()
  }
  setCloud(cloud: CloudFilter) {
    if (this.cloud === cloud) return
    this.cloud = cloud
    this.expandedApps.clear()
    this.scheduleRender()
  }
  setTab(tab: Tab) {
    if (tab === 'trend' && !this.trendAvailable) return
    if (this.activeTab === tab) return
    this.activeTab = tab
    // The breakdown is only fetched for the tab that shows it, so opening it is a
    // trigger in its own right.
    this.ensureBreakdownCoverage()
    // Let the tab's content mount before measuring/redrawing its chart.
    setTimeout(() => {
      this.setupResizeObserver()
      this.renderAll()
    }, 0)
  }
  setGranularity(g: Granularity) {
    // Monthly needs ≥2 monthly points to draw a trend; ignore the switch on
    // ranges too short to produce one (would strand a single dot on the chart).
    if (g === 'monthly' && !this.canViewMonthly) return
    // Daily is capped at the span the auto-selection already uses, which also bounds
    // how many cycles a period can ask the backend for at once.
    if (g === 'daily' && !this.canViewDaily) return
    if (this.granularity === g) return
    this.granularityOverride = g
    this.scheduleRender()
  }
  private scheduleRender() {
    // Before the coverage calls: they only fetch for the tab that shows their data.
    const switchedTab = this.syncTabToTrendAvailability()
    this.ensureDailyCoverage()
    this.ensureBreakdownCoverage()
    setTimeout(() => {
      // Restoring the trend tab builds a fresh chart element; without the rebind the
      // observer stays attached to the detached one and resizes stop redrawing.
      if (switchedTab) this.setupResizeObserver()
      this.renderAll()
    }, 0)
  }

  /**
   * Pull the per-application detail the selected period needs but hasn't loaded.
   *
   * apps.json serves one cycle per request, so a period that doesn't overlap the cycle
   * loaded at startup found no rows at all and the table read "No application data for
   * this period". Deferred until the tab is actually opened, since nothing outside it
   * reads the breakdown.
   */
  private ensureBreakdownCoverage() {
    const accountId = this.accountService.accountId
    if (!accountId || this.activeTab !== 'apps' || !this.periodStart) return
    const missing = this.cycleKeysInRange(MAX_FETCHED_BREAKDOWN_CYCLES).filter(
      (key) => !this.loadedBreakdownCycles.has(key)
    )
    if (!missing.length) return
    missing.forEach((key) => this.loadedBreakdownCycles.add(key))
    this.appsLoading = true
    this.usageBillingService
      .loadAppBreakdownForCycles(accountId, missing)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (rows) => {
          this.mergeBreakdown(rows)
          this.appsLoading = false
        },
        error: (err) => {
          // Marked loaded before the request so a mid-flight period change doesn't refetch;
          // roll that back on failure or one transient error strands these cycles as "loaded"
          // and the peak day silently resolves against a partial set for the rest of the session.
          missing.forEach((key) => this.loadedBreakdownCycles.delete(key))
          this.appsLoading = false
          handleArpioError(window.location.href, this.apiService, this.uiService)(err)
        },
      })
  }

  /** Merge fetched breakdown days in, keeping the set date-sorted and duplicate-free. */
  private mergeBreakdown(rows: DailyBreakdownRowVm[]) {
    if (!rows.length) return
    const byDate = new Map(this.breakdownRows.map((r) => [r.date, r]))
    rows.forEach((r) => byDate.has(r.date) || byDate.set(r.date, r))
    this.breakdownRows = [...byDate.values()].sort((a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : 0))
    this.breakdownFloor = this.breakdownRows[0]?.date ?? ''
  }

  /**
   * Pull the cycles the selected period needs but hasn't loaded.
   *
   * The usage endpoint serves one billing cycle per request, so the dashboard's initial
   * load only covers the current one. Without this, every daily period floored back to
   * that cycle and the chart looked frozen — picking "30 days" mid-cycle redrew the
   * exact same days it was already showing.
   */
  private ensureDailyCoverage() {
    const accountId = this.accountService.accountId
    if (!accountId || this.granularity !== 'daily' || !this.periodStart) return
    const missing = this.cycleKeysInRange(MAX_FETCHED_DAILY_CYCLES).filter((key) => !this.loadedCycles.has(key))
    if (!missing.length) return
    // Marked before the request so a second period change mid-flight doesn't refetch.
    missing.forEach((key) => this.loadedCycles.add(key))
    this.chartLoading = true
    forkJoin(
      // A cycle outside the backend's retention 404s; that's a gap in the series, not a
      // failed page, and the truncation notice already explains a short series.
      missing.map((key) => this.usageBillingService.loadDailyForCycle(accountId, key).pipe(catchError(() => of([]))))
    )
      .pipe(takeUntil(this.destroy$))
      .subscribe((batches) => {
        this.mergeDaily(batches.flat())
        this.chartLoading = false
        setTimeout(() => this.renderAll(), 0)
      })
  }

  /** Merge fetched days into the series, keeping it date-sorted and duplicate-free. */
  private mergeDaily(days: DailyPointVm[]) {
    if (!days.length) return
    const byDate = new Map(this.allDaily.map((d) => [d.date, d]))
    // Cycles can overlap a calendar month, so the same day may arrive twice; the day
    // already held wins, keeping the initially loaded cycle authoritative.
    days.forEach((d) => byDate.has(d.date) || byDate.set(d.date, d))
    this.allDaily = [...byDate.values()].sort((a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : 0))
    this.dailyFloor = this.allDaily[0]?.date ?? ''
  }

  /**
   * The key (YYYY-MM) of the billing cycle a given day belongs to.
   *
   * A cycle is named by the month it starts in, so for an anniversary-billed account a
   * date before the anchor day belongs to the cycle that opened the previous month. This
   * is the only correct cycle key — `billingCycle.monthKey` echoes the requested calendar
   * month, which diverges from the start month for an anniversary cycle spanning the
   * boundary, so keying loads off it skips (or redundantly refetches) the real cycle.
   *
   * Returns '' for a missing/malformed date rather than producing an Invalid Date; the
   * caller falls back to `billingCycle.monthKey`.
   */
  private cycleKeyForDate(iso: string): string {
    const [year, month, day] = (iso ?? '').split('-').map(Number)
    if (!year || !month || !day) return ''
    const rawAnchor = Number(this.vm?.billingCycle.start.slice(8, 10)) || 1
    // An anchor of 29–31 lands past the end of shorter months; the server clamps the cycle
    // start to the last day, so anchor-day matching has to clamp the same way or every day
    // of a short month reads as belonging to the previous cycle.
    const daysInMonth = new Date(Date.UTC(year, month, 0)).getUTCDate()
    const anchorDay = Math.min(rawAnchor, daysInMonth)
    return this.iso(new Date(Date.UTC(year, month - 1 + (day >= anchorDay ? 0 : -1), 1))).slice(0, 7)
  }

  /**
   * The cycle keys covering the selected period, oldest first, up to `max` of them.
   *
   * Walked backwards from the period end so that a range wider than `max` keeps the most
   * recent cycles. The daily view uses a small cap (it only ever plots ~2 months); the
   * breakdown uses a wide one so its peak day is resolved across the whole selection.
   */
  private cycleKeysInRange(max: number): string[] {
    const first = this.cycleKeyForDate(this.periodStart)
    const cursor = new Date(`${this.cycleKeyForDate(this.periodEnd || this.periodStart)}-01T00:00:00Z`)
    const keys: string[] = []
    while (keys.length < max) {
      const key = this.iso(cursor).slice(0, 7)
      keys.unshift(key)
      if (key <= first) break
      cursor.setUTCMonth(cursor.getUTCMonth() - 1)
    }
    return keys
  }

  // --- derived period slices ------------------------------------------------

  get spanDays(): number {
    if (!this.periodStart || !this.periodEnd) return 0
    return Math.round((Date.parse(this.periodEnd) - Date.parse(this.periodStart)) / 86400000) + 1
  }
  get granularity(): Granularity {
    return this.granularityOverride ?? (this.spanDays <= DAILY_MAX_SPAN_DAYS ? 'daily' : 'monthly')
  }
  /** Monthly view is only meaningful with ≥2 monthly points to connect. */
  get canViewMonthly(): boolean {
    return this.monthsInRange.length >= 2
  }
  /** Daily plots a point per day, so it's capped at the span the auto-selection uses. */
  get canViewDaily(): boolean {
    return this.spanDays <= DAILY_MAX_SPAN_DAYS
  }
  /** Why Daily is disabled. Empty when enabled. */
  get dailyDisabledReason(): string {
    return this.canViewDaily ? '' : `Select a range of ${DAILY_MAX_SPAN_DAYS} days or less to view daily`
  }
  /** Why Monthly is disabled — distinguishes "no trend data at all" (the monthly
   *  endpoint is unavailable) from "this range is too short". Empty when enabled. */
  get monthlyDisabledReason(): string {
    if (this.canViewMonthly) return ''
    return (this.vm?.monthly.length ?? 0) === 0
      ? 'Monthly trend data is not available for this account yet'
      : 'Select a range spanning at least two months to view monthly'
  }

  private get daysInRange(): DailyPointVm[] {
    const start = this.effectiveStart
    return this.allDaily.filter((d) => d.date >= start && d.date <= this.periodEnd)
  }
  private get monthsInRange(): MonthlyPointVm[] {
    const s = this.periodStart.slice(0, 7)
    const e = this.periodEnd.slice(0, 7)
    return (this.vm?.monthly ?? []).filter((m) => m.monthKey >= s && m.monthKey <= e)
  }

  // Cloud-adjusted accessors.
  private mHwm(m: MonthlyPointVm): number {
    return this.cloud === 'AWS' ? m.awsHwm : this.cloud === 'AZURE' ? m.azureHwm : m.hwm
  }
  private dBill(d: DailyPointVm): number {
    return this.cloud === 'AWS' ? d.awsBillable : this.cloud === 'AZURE' ? d.azureBillable : d.billable
  }
  private dTotal(d: DailyPointVm): number {
    return this.cloud === 'AWS' ? d.awsTotal : this.cloud === 'AZURE' ? d.azureTotal : d.total
  }

  // --- stats tiles ----------------------------------------------------------
  // The stat cards bind directly to vm.kpis (the current-billing-period snapshot)
  // in the template, so they stay fixed regardless of the period/cloud filter.
  // `contracted` is also read by the trend chart below.

  get contracted(): number | null {
    return this.vm?.kpis.contracted ?? null
  }

  /**
   * Badges the header only for Flex. Everything else — Fixed, legacy, trial, or a server
   * that never sent `consumptionEnabled` — renders no badge rather than asserting a mode
   * we can't confirm. Fixed billing has its own dashboard treatment and
   * mislabelling here would promise enforcement semantics this page doesn't implement.
   */
  get isFlexBilling(): boolean {
    return this.accountService.flexBillingEnabled
  }

  /**
   * True when the cycle recorded no billable usage at all. Daily HWM snapshots are
   * built from completed backup jobs, so an account with applications that aren't
   * backing up reports zeros everywhere — which reads as a broken page unless we say
   * why the numbers are empty.
   */
  get noBillableUsage(): boolean {
    // A cycle with no snapshots yet reports null rather than zero, and needs the same
    // explanation — the tiles are empty either way.
    return !!this.vm && this.vm.kpis.monthlyHwm === 0 && !this.vm.kpis.currentBillable
  }

  /** Stamped by the last render rather than recomputed, so the legend's dashed swatch can
   *  never promise a line the chart didn't draw. */
  contractedLineVisible = false

  /** Headroom against the contracted cap. A getter rather than a template expression: the
   *  number and the word after it change together, and composing them in a binding would
   *  allocate a fresh string every change-detection pass. */
  get headroomLabel(): string {
    if (!this.vm?.kpis) return ''
    const { within, delta } = this.vm.kpis
    return within ? `${formatCount(-delta)} available` : `${formatCount(delta)} over contract`
  }

  // --- Application breakdown (Applications tab) -----------------------------

  /**
   * The day the breakdown reflects: the peak-billable day within the selected period.
   *
   * Drawn from the cycles fetched so far, which is capped, so a period spanning more
   * cycles than that resolves its peak from the most recent ones — `breakdownRangeTruncated`
   * says so rather than letting the day read as the whole period's peak.
   */
  private get breakdownDay() {
    const rows = this.breakdownRows.filter((r) => r.date >= this.periodStart && r.date <= this.periodEnd)
    return rows.length ? rows.reduce((max, r) => (r.billable > max.billable ? r : max), rows[0]) : null
  }
  /**
   * True when the selection reaches back past the per-application detail. The table
   * still shows the peak day it has, so without this the period filter reads as
   * though it searched the whole range. Measured from `recordedRangeStart`: days that
   * predate the product's records aren't detail we failed to fetch.
   */
  get breakdownRangeTruncated(): boolean {
    return this.hasBreakdownData && !!this.breakdownFloor && this.recordedRangeStart < this.breakdownFloor
  }

  /** Earliest selected day that could hold records, keeping "we didn't fetch back that
   *  far" distinct from "nothing was ever recorded back there". */
  private get recordedRangeStart(): string {
    return this.periodStart > USAGE_DATA_START_DATE ? this.periodStart : USAGE_DATA_START_DATE
  }
  /** Names the period the CSV covers — the export follows the selected date range. */
  get exportLabel(): string {
    if (!this.periodStart || !this.periodEnd) return 'Export to CSV'
    return `Export CSV for ${this.formatDay(this.periodStart)} – ${this.formatDay(this.periodEnd)}`
  }
  get breakdownDayLabel(): string {
    return this.breakdownDay?.dayLabel ?? '—'
  }
  get hasBreakdownData(): boolean {
    return this.breakdownDay != null
  }

  /** Why the breakdown table is empty. Most specific first: a selection landing wholly
   *  before the records explains itself, where the generic copy would read as a fault. An
   *  account whose every application sat idle is accounted for by count, so an empty table
   *  reads as an explained fact rather than detail the dashboard failed to load. */
  get appsEmptyMessage(): string {
    if (this.rangeBeforeUsageData) return this.noUsageDataMessage
    if (!this.hasBreakdownData) return 'No application data for this period.'
    if (this.appSearch) return 'No applications match the current filters.'
    const idle = this.idleAppCount
    if (idle) return `${formatCount(idle)} application${idle === 1 ? '' : 's'} with no billable resources not listed.`
    if (this.cloud !== 'ALL') return 'No applications match the current filters.'
    return 'No application data available.'
  }

  /** Apps on the breakdown day (the period's peak-billable day — see above), highest first. */
  get appBreakdown(): AppBreakdownRowVm[] {
    return this.breakdownDay?.topApps ?? []
  }

  /**
   * Rows after the global cloud filter + the search box, capped for display.
   *
   * The cap has to come last: applied to the unfiltered list it would hide apps the
   * filter is specifically looking for — an account whose ten busiest apps are all AWS
   * would show an empty table under the Azure filter while the KPI tile counts Azure
   * apps on the same screen.
   */
  get filteredAppBreakdown(): AppBreakdownRowVm[] {
    this.ensureAppFilterCache()
    return this._displayedApps
  }

  /** Everything the filters leave, uncapped — the basis both cap notices are measured against. */
  private get qualifyingApps(): AppBreakdownRowVm[] {
    this.ensureAppFilterCache()
    return this._qualifyingApps
  }

  /**
   * Filter once per input change rather than once per reader. The rows, both cap notices,
   * the bar denominator and the expand-all state all read these sets within a single
   * change-detection pass, and the backend caps nothing — topApps carries every
   * application that billed, so an unmemoised getter rescans the full list on every event.
   */
  private ensureAppFilterCache() {
    const source = this.appBreakdown
    const query = this.appSearch.trim().toLowerCase()
    const key = this._appFilterKey
    if (source === this._appFilterSource && key != null && key[0] === this.cloud && key[1] === query) return
    this._appFilterSource = source
    this._appFilterKey = [this.cloud, query]
    this._qualifyingApps = source.filter(
      (a) => (this.cloud === 'ALL' || a.cloud === this.cloud) && this.appMatches(a, query)
    )
    this._displayedApps = this._qualifyingApps.slice(0, APP_BREAKDOWN_LIMIT)
  }

  /**
   * The account's own application count, narrowed to the active cloud so the note's
   * arithmetic matches the rows beside it. Read from the account's application list rather
   * than the usage snapshot, which only ever lists applications that billed.
   */
  private get ownedAppCount(): number {
    const kpis = this.vm?.kpis
    if (!kpis) return 0
    if (this.cloud === 'AWS') return kpis.appsAws
    if (this.cloud === 'AZURE') return kpis.appsAzure
    return kpis.appsTotal
  }

  /**
   * Applications the account owns that billed nothing, so listed rows plus the excluded
   * counts reconcile with the account total. Clamped at zero: an application deleted after
   * the day the table reports still sits in that day's snapshot while the account list has
   * moved on, which would drive the subtraction negative.
   */
  private get idleAppCount(): number {
    const idle = this.ownedAppCount - this.qualifyingApps.length
    return idle > 0 ? idle : 0
  }

  /**
   * One accounting of everything the table withholds — the cap's share and the idle
   * applications' share in a single statement, since two notes side by side read as
   * competing counts. Silent under a search, which narrows the list deliberately and has no
   * account total to reconcile against. Composed here rather than in the template: the
   * counts and the words between them change together.
   */
  get hiddenAppsNote(): string {
    if (this.appSearch.trim() || !this.hasBreakdownData) return ''
    // With no rows at all the empty-state cell carries the same accounting; a footer note
    // under an empty table would state it twice.
    if (!this.filteredAppBreakdown.length) return ''
    const hidden = this.qualifyingApps.slice(APP_BREAKDOWN_LIMIT)
    const idle = this.idleAppCount
    const clauses: string[] = []
    if (hidden.length) {
      const resources = hidden.reduce((sum, a) => sum + a.billableCount, 0)
      clauses.push(
        `${formatCount(hidden.length)} application${hidden.length === 1 ? '' : 's'} not shown — ` +
          `${formatCount(resources)} billable resource${resources === 1 ? '' : 's'} combined`
      )
    }
    if (idle) {
      // "more" only once a cap clause precedes it, so the two counts tally rather than
      // reading as two answers to the same question.
      const subject = clauses.length
        ? `${formatCount(idle)} more`
        : `${formatCount(idle)} application${idle === 1 ? '' : 's'}`
      clauses.push(`${subject} had no billable resources during this time, so they are not listed`)
    }
    return clauses.length ? `${clauses.join('. ')}.` : ''
  }

  private appMatches(a: { appName: string; cloud: string; byType: { resourceType: string }[] }, q: string): boolean {
    if (!q) return true
    return (
      a.appName.toLowerCase().includes(q) ||
      a.cloud.toLowerCase().includes(q) ||
      a.byType.some((t) => t.resourceType.toLowerCase().includes(q))
    )
  }

  /** Bar denominator — largest displayed value, for row comparison. Never zero: an
   *  all-zero set would divide the bar widths into NaN, which CSS drops, filling every
   *  bar rather than emptying it. */
  get maxAppBillable(): number {
    const f = this.filteredAppBreakdown
    return f.length ? f[0].billableCount || 1 : 1
  }

  // --- expandable rows ------------------------------------------------------

  get allExpanded(): boolean {
    const apps = this.filteredAppBreakdown
    return apps.length > 0 && apps.every((r) => this.expandedApps.has(r.appId))
  }
  toggleAll() {
    if (this.allExpanded) this.expandedApps.clear()
    else this.filteredAppBreakdown.forEach((r) => this.expandedApps.add(r.appId))
  }
  toggleApp(appId: string) {
    this.expandedApps.has(appId) ? this.expandedApps.delete(appId) : this.expandedApps.add(appId)
  }
  isAppExpanded(appId: string): boolean {
    return this.expandedApps.has(appId)
  }

  trackByAppId = (_: number, row: { appId: string }) => row.appId
  trackByType = (_: number, row: { resourceType: string }) => row.resourceType

  // --- contract details -----------------------------------------------------

  get entitlementLoaded(): boolean {
    return this.accountService.entitlementLoaded
  }

  get productEdition(): string {
    return this.accountService.productEdition
  }

  get subscriptionExpiration(): Date {
    return this.accountService.subscriptionExpiration
  }

  get subscriptionExpired(): boolean {
    return this.accountService.subscriptionExpired
  }

  // --- CSV export ----------------------------------------------------------

  /**
   * Server-generated per-application usage CSV. Same-origin GET with cookies; the
   * server sets Content-Disposition so the browser downloads it. Bounds are inclusive on both
   * sides, matching the table's own period filter.
   */
  private downloadAppUsageCsv(startDate: string, endDate: string) {
    const accountId = this.accountService.accountId
    if (!accountId || !startDate || !endDate) return
    const url =
      this.apiService.buildUrl(['api', 'accounts', accountId, 'usage_report', 'apps.csv']) +
      `?startDate=${startDate}&endDate=${endDate}`
    const a = document.createElement('a')
    a.href = url
    a.rel = 'noopener'
    a.click()
  }

  /**
   * The whole selected period. Sends the raw selection rather than `effectiveStart` — that
   * floors to the daily window this browser happens to have cached, which would truncate an
   * export that can reach the full retention.
   */
  exportCsv() {
    this.downloadAppUsageCsv(this.periodStart, this.periodEnd)
  }

  /** Just the day the table reports, so the file matches what is on screen. */
  exportBreakdownDayCsv() {
    const day = this.breakdownDay?.date
    if (day) this.downloadAppUsageCsv(day, day)
  }

  /**
   * The split button's dropdown. Returns a stable array so PrimeNG isn't handed a new model
   * on every change-detection pass, recomposing the second label only when the reported day
   * actually moves.
   */
  get exportMenuItems(): MenuItem[] {
    const dayLabel = this.breakdownDayLabel
    if (dayLabel !== this._exportMenuDayLabel) {
      this._exportMenuDayLabel = dayLabel
      this._exportMenuItems[1].label =
        `${dayLabel} only` + '<div class="menu-desc">Every application on the day the table reports</div>'
    }
    return this._exportMenuItems
  }

  // --- charts ---------------------------------------------------------------

  private setupResizeObserver() {
    this.resizeObserver?.disconnect()
    this.resizeObserver = new ResizeObserver(() => this.renderTrend())
    if (this.trendChart) this.resizeObserver.observe(this.trendChart.nativeElement)
  }

  private renderAll() {
    this.renderTrend()
  }

  private renderTrend() {
    if (!this.trendChart || this.activeTab !== 'trend') return
    const contracted = this.contracted ?? null

    if (this.granularity === 'daily') {
      const days = this.daysInRange
      const capInScale = capFitsScale(d3.max(days.map((d) => this.dBill(d))) || 0, contracted)
      this.renderLine(this.trendChart.nativeElement, {
        points: days.map((d) => {
          const value = this.dBill(d)
          return {
            x: d.dayLabel,
            value,
            over: contracted != null && value > contracted,
            tooltip: [
              `<strong>${d.dayLabel}</strong>`,
              `Billable: ${formatCount(value)}`,
              `Total: ${formatCount(this.dTotal(d))}`,
            ],
          }
        }),
        contracted,
        capInScale,
        yTitle: 'Billable resources',
      })
      return
    }

    const months = this.monthsInRange
    const capInScale = capFitsScale(d3.max(months.map((m) => this.mHwm(m))) || 0, contracted)
    this.renderLine(this.trendChart.nativeElement, {
      points: months.map((m) => {
        const value = this.mHwm(m)
        return {
          x: m.label,
          value,
          over: contracted != null && value > contracted,
          // Off the scale the cap is already named once in the corner; repeating it on every
          // hover restates a line the chart deliberately isn't drawing.
          tooltip: [
            `<strong>${m.label}</strong>`,
            `High water mark: ${formatCount(value)}`,
            ...(capInScale ? [`Contracted: ${formatCount(m.contracted)}`] : []),
          ],
        }
      }),
      contracted,
      capInScale,
      yTitle: 'Billable resources (HWM)',
    })
  }

  private renderLine(container: HTMLDivElement, series: ChartSeries) {
    const width = container.clientWidth
    // Measured, not restated: the container is shorter at the mobile breakpoint, and a
    // fixed height drew the x-axis and its labels past the bottom of the box.
    const height = container.clientHeight || 300
    if (width === 0) return

    d3.select(container).selectAll('svg').remove()
    d3.select(container).selectAll('.chart-tooltip').remove()

    // The domain and its labels come first so the left margin can be sized to them.
    // `.nice()` and `.ticks()` read the domain only, so none of this needs the range.
    const capInScale = series.capInScale
    this.contractedLineVisible = capInScale && series.contracted != null && series.contracted > 0
    const values = series.points.map((p) => p.value)
    const dataMax = d3.max(values) || 0
    const maxVal = Math.max(dataMax, capInScale ? series.contracted! : 0)
    const yMax = Math.ceil(maxVal * 1.15) || 10
    const yScale = d3.scaleLinear().domain([0, yMax]).nice(Y_TICK_COUNT)
    const yTicks = integerTicks(yScale, Y_TICK_COUNT)
    // One unit for the whole axis — a bare `800` beside `1k` reads as two different scales.
    const tickFormat = (yTicks.find((t) => t > 0) ?? 0) >= 1000 ? AXIS_SI_FORMAT : GROUPED_COUNT_FORMAT
    const yTickLabels = yTicks.map(tickFormat)

    const margin = { top: 24, right: 20, bottom: 44, left: axisLeftMargin(yTickLabels) }
    const innerWidth = width - margin.left - margin.right
    const innerHeight = height - margin.top - margin.bottom

    const svg = d3
      .select(container)
      .append('svg')
      .attr('data-cy', 'usage-trend-svg')
      .attr('width', width)
      .attr('height', height)
    const g = svg.append('g').attr('transform', `translate(${margin.left},${margin.top})`)

    // Index-based linear x so a d3-zoom transform can rescale it (scalePoint has
    // no invert / rescale). Half-step padding keeps the original centered look.
    const xScale = d3
      .scaleLinear()
      .domain([-0.5, series.points.length - 0.5])
      .range([0, innerWidth])
    yScale.range([innerHeight, 0])

    const labelAt = (i: number) => series.points[i]?.x ?? ''
    const step = Math.max(1, Math.ceil(series.points.length / 12))
    const allTickIdx = series.points.map((_, i) => i).filter((i) => i % step === 0)

    // Clip the plotted geometry to the axes box so zoomed/panned content can't
    // bleed over the margins or axis labels.
    svg
      .append('defs')
      .append('clipPath')
      .attr('id', this.clipId)
      .append('rect')
      .attr('width', innerWidth)
      .attr('height', innerHeight)

    // Redrawable x-axis: only ticks whose position lands inside the plot show,
    // so labels don't spill past the edges when zoomed in.
    const xAxisG = g.append('g').attr('transform', `translate(0,${innerHeight})`)
    const drawXAxis = (sx: d3.ScaleLinear<number, number>) => {
      const ticks = allTickIdx.filter((i) => sx(i) >= 0 && sx(i) <= innerWidth)
      xAxisG
        .call(
          d3
            .axisBottom<number>(sx)
            .tickValues(ticks)
            .tickFormat((d) => labelAt(d as number))
        )
        .selectAll('text')
        .style('font-size', '11px')
        .style('fill', AXIS)
    }

    g.append('g')
      .call(
        d3
          .axisLeft(yScale)
          .tickValues(yTicks)
          .tickFormat((_d, i) => yTickLabels[i])
      )
      .selectAll('text')
      .style('font-size', '11px')
      .style('fill', AXIS)

    g.append('text')
      .attr('transform', 'rotate(-90)')
      .attr('x', -innerHeight / 2)
      .attr('y', -(margin.left - AXIS_TITLE_INSET))
      .attr('text-anchor', 'middle')
      .attr('fill', GRAY)
      .style('font-size', '12px')
      .text(series.yTitle)

    if (series.contracted != null && series.contracted > 0 && !capInScale) {
      // The cap sits far enough above the data that plotting it would flatten the series
      // onto the axis, so it is named here instead. Anchored left on a narrow chart, where
      // a right-anchored label of this length would run back over the y-axis ticks.
      const label = `Contracted: ${formatCount(series.contracted)} (off scale)`
      const fitsRight = label.length * 6 <= innerWidth
      g.append('text')
        .attr('x', fitsRight ? innerWidth : 0)
        .attr('y', 12)
        .attr('text-anchor', fitsRight ? 'end' : 'start')
        .attr('fill', GRAY)
        .style('font-size', '11px')
        .text(label)
    } else if (series.contracted != null && series.contracted > 0) {
      const y = yScale(series.contracted)
      g.append('line')
        .attr('x1', 0)
        .attr('x2', innerWidth)
        .attr('y1', y)
        .attr('y2', y)
        .attr('stroke', BASELINE)
        .attr('stroke-width', 1.5)
        .attr('stroke-dasharray', '6,4')
      g.append('text')
        .attr('x', innerWidth)
        .attr('y', y - 6)
        .attr('text-anchor', 'end')
        .attr('fill', GRAY)
        .style('font-size', '11px')
        .text(`Contracted: ${formatCount(series.contracted)}`)
    }

    const yOf = (p: ChartPoint) => yScale(p.value)

    const tooltip = d3
      .select(container)
      .append('div')
      .attr('class', 'chart-tooltip')
      .style('position', 'absolute')
      .style('background', '#fff')
      .style('border', `1px solid ${BASELINE}`)
      .style('border-radius', '4px')
      .style('padding', '8px 12px')
      .style('font-size', '12px')
      .style('pointer-events', 'none')
      .style('box-shadow', '0 2px 6px rgba(0,0,0,0.12)')
      .style('opacity', 0)

    // Plotted geometry lives in a clipped layer so pan/zoom stays inside the axes.
    const plot = g.append('g').attr('clip-path', `url(#${this.clipId})`)

    const areaGen = d3.area<ChartPoint>().y0(innerHeight).y1(yOf).curve(d3.curveMonotoneX)
    const areaPath = plot.append('path').datum(series.points).attr('fill', ACCENT).attr('fill-opacity', 0.08)

    const lineGen = d3.line<ChartPoint>().y(yOf).curve(d3.curveMonotoneX)
    const linePath = plot
      .append('path')
      .datum(series.points)
      .attr('data-cy', 'usage-trend-line')
      .attr('fill', 'none')
      .attr('stroke', ACCENT)
      .attr('stroke-width', 2)

    const dots = plot
      .selectAll('.data-point')
      .data(series.points)
      .enter()
      .append('circle')
      .attr('class', 'data-point')
      .attr('data-cy', 'usage-trend-point')
      .attr('data-label', (d) => d.x)
      .attr('data-value', (d) => d.value)
      .attr('cy', yOf)
      .attr('r', 4)
      .attr('fill', (d) => (d.over ? AMBER : ACCENT))

    // Reposition every x-dependent mark for a given (possibly zoomed) x-scale.
    let currentX = xScale
    const redraw = (sx: d3.ScaleLinear<number, number>) => {
      currentX = sx
      areaGen.x((_d, i) => sx(i))
      lineGen.x((_d, i) => sx(i))
      areaPath.attr('d', areaGen)
      linePath.attr('d', lineGen)
      dots.attr('cx', (_d, i) => sx(i))
      drawXAxis(sx)
    }
    redraw(xScale)

    // Transparent catcher on top drives wheel-zoom + drag-pan and resolves the
    // hover by nearest x-index (dots sit beneath it and can't receive events).
    const overlay = g
      .append('rect')
      .attr('class', 'zoom-catcher')
      .attr('width', innerWidth)
      .attr('height', innerHeight)
      .style('fill', 'none')
      .style('pointer-events', 'all')
      .style('cursor', 'grab')

    const showTooltipAt = (event: MouseEvent) => {
      if (!series.points.length) return
      const [mx] = d3.pointer(event, overlay.node())
      const i = Math.max(0, Math.min(series.points.length - 1, Math.round(currentX.invert(mx))))
      const [cx, cy] = d3.pointer(event, container)
      tooltip
        .html(series.points[i].tooltip.join('<br>'))
        .style('opacity', 1)
        .style('left', `${cx + 14}px`)
        .style('top', `${cy + 14}px`)
    }
    overlay.on('mousemove', showTooltipAt).on('mouseout', () => tooltip.style('opacity', 0))

    const zoom = d3
      .zoom<SVGRectElement, unknown>()
      .scaleExtent([1, 10])
      .translateExtent([
        [0, 0],
        [innerWidth, innerHeight],
      ])
      .extent([
        [0, 0],
        [innerWidth, innerHeight],
      ])
      // Wheel only zooms with a modifier held — a plain wheel must fall through to
      // page scroll, or the chart traps the scroll and you can't leave the page.
      .filter((event) => (event.type === 'wheel' ? event.ctrlKey || event.metaKey : !event.button))
      .on('start', () => overlay.style('cursor', 'grabbing'))
      .on('zoom', (event) => redraw(event.transform.rescaleX(xScale)))
      .on('end', () => overlay.style('cursor', 'grab'))

    // Double-click resets to the full range instead of d3's default zoom-in.
    overlay.call(zoom).on('dblclick.zoom', null)
    overlay.on('dblclick', () => overlay.transition().duration(200).call(zoom.transform, d3.zoomIdentity))
  }
}

// Whether the contracted cap belongs in the chart's y domain. A cap more than double the
// observed peak buys no readability — it just compresses the series against the axis — so
// past that it is annotated rather than plotted. A cycle with no usage at all is the
// exception: there is no shape to reveal, and the cap line beats an empty axis.
function capFitsScale(dataMax: number, contracted: number | null | undefined): boolean {
  if (contracted == null || contracted <= 0) return false
  return dataMax === 0 || contracted <= dataMax * CAP_IN_SCALE_RATIO
}

// d3's tick values with the fractions dropped: resource counts are whole numbers, and a
// small domain otherwise subdivides into halves that read as "1.5 billable resources".
// Every tick lands on a round 1/2/5 boundary, which is what lets the SI format name it
// exactly. Pair with `.nice(count)` or the axis stops short of the domain top.
function integerTicks(scale: d3.ScaleLinear<number, number>, count: number): number[] {
  return scale.ticks(count).filter(Number.isInteger)
}

// Rough advance width per character at the axis's 11px, plus d3's tick size and padding
// and a band for the rotated title. Estimated rather than measured: a real getBBox() here
// would force a layout on every render, and the axis only needs to not collide.
function axisLeftMargin(labels: string[]): number {
  const widest = labels.reduce((max, l) => Math.max(max, l.length), 0) * 6.5
  return Math.min(96, Math.max(56, Math.ceil(widest) + 9 + AXIS_TITLE_INSET + 4))
}
